import React, { useState, useEffect } from 'react';
import { useAlert } from '../../../providers/AlertProvider';
import { useAuthProvider } from '../../../providers/AuthProvider';
import { validatePassword } from "../../../api/profiles";
import logger from "../../../utils/logger";

import RequestPassword from "../RequestPassword";
import "../../css/GlobalStyles.css";

const DriverRoutesReport = ({ routeData = [], theme }) => {
    const { profile } = useAuthProvider();
    const { showAlert } = useAlert();

    const [tableData, setTableData] = useState([]);
    const [isFiltered, setIsFiltered] = useState(false);

    const isLightTheme = theme === 'light';

    const displayColumns = [
        { key: 'date', label: 'Date' },
        { key: 'driver_name', label: 'Driver' },
        { key: 'customer', label: 'Customer' },
        { key: 'route', label: 'Route' },
        { key: 'vehicle_reg', label: 'Vehicle' },
        { key: 'comments', label: 'Comments' },
    ];

    // Request Password States =======================================================================================================
    const [openRequestPassword, setOpenRequestPassword] = useState(false);

    useEffect(() => {
        setOpenRequestPassword(true);
        //showData(); // for debugging
    }, []);
    
    const handlePasswordSubmission = async (password) => {
        const email = profile.email;
    
        try {
            const { status } = await validatePassword(password, email);
            if (status === 200) {
                showData();
            } else {
                logger.log("Invalid password.");
                routeData.length = 0;
                showAlert({
                    message: 'Invalid password. Access denied.',
                    type: 'danger',
                    buttons: [{
                        type: 'danger',
                        label: 'Close',
                    }],
                });
            }
        } catch (error) {
            logger.error("Error validating password:", error);
            showAlert({
                message: 'An error occurred while validating the password.',
                type: 'danger',
                buttons: [{
                    type: 'danger',
                    label: 'Close',
                }],
            });
        }
        setOpenRequestPassword(false);
    };

    const showData = () => {
        logger.log("Route data received:", routeData);
        
        if (!Array.isArray(routeData) || routeData.length === 0) {
            logger.log("No route data available");
            setTableData([]);
            setIsFiltered(true);
            return;
        }

        setIsFiltered(true);

        // Format filtered data
        const formatted = routeData.map((entry) => {
            const newEntry = {};
            displayColumns.forEach(({ key }) => {
                if (key === 'date') {
                    const dateObj = new Date(entry[key]);
                    newEntry[key] = isNaN(dateObj)
                        ? entry[key]
                        : `${String(dateObj.getDate()).padStart(2, '0')}-${String(dateObj.getMonth() + 1).padStart(2, '0')}-${dateObj.getFullYear()}`;
                } else {
                    newEntry[key] = entry[key] ?? '—';
                }
            });
            return newEntry;
        });

        setTableData(formatted);
    };

    return (
        <div>
            <RequestPassword
                theme={theme}
                isOpen={openRequestPassword}
                setIsOpen={setOpenRequestPassword}
                returnData={handlePasswordSubmission}
            />

            <div style={{ marginBottom: '20px', textAlign: 'center' }}>
                <h3>Driver Routes Report</h3>
            </div>

            {isFiltered && (
                <div style={{ 
                    maxHeight: '70vh', 
                    overflowY: 'auto',
                    border: `1px solid ${isLightTheme ? '#ddd' : '#555'}`,
                    borderRadius: '8px'
                }}>
                    <table style={{ 
                        width: '100%', 
                        borderCollapse: 'collapse',
                        backgroundColor: isLightTheme ? '#fff' : '#333'
                    }}>
                        <thead>
                            <tr style={{ 
                                backgroundColor: isLightTheme ? '#f8f9fa' : '#fff',
                                position: 'sticky',
                                top: 0,
                                zIndex: 1
                            }}>
                                {displayColumns.map(({ key, label }) => (
                                    <th
                                        key={key}
                                        style={{
                                            padding: '12px 8px',
                                            borderBottom: `2px solid ${isLightTheme ? '#ddd' : '#555'}`,
                                            textAlign: 'center',
                                            fontWeight: 'bold',
                                            color: isLightTheme ? '#333' : '#000'
                                        }}
                                    >
                                        {label}
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {tableData.length > 0 ? (
                                tableData.map((row, index) => (
                                    <tr
                                        key={index}
                                        style={{
                                            backgroundColor: index % 2 === 0 
                                                ? (isLightTheme ? '#f9f9f9' : '#3a3a3a')
                                                : (isLightTheme ? '#fff' : '#333'),
                                            '&:hover': {
                                                backgroundColor: isLightTheme ? '#e9ecef' : '#495057'
                                            }
                                        }}
                                    >
                                        {displayColumns.map(({ key }) => (
                                            <td
                                                key={key}
                                                style={{
                                                    padding: '10px 8px',
                                                    borderBottom: `1px solid ${isLightTheme ? '#eee' : '#555'}`,
                                                    color: isLightTheme ? '#333' : '#fff'
                                                }}
                                            >
                                                {row[key]}
                                            </td>
                                        ))}
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td
                                        colSpan={displayColumns.length}
                                        style={{
                                            padding: '20px',
                                            textAlign: 'center',
                                            color: isLightTheme ? '#666' : '#ccc',
                                            fontStyle: 'italic'
                                        }}
                                    >
                                        No driver routes data available
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
};

export default DriverRoutesReport;
